import { RegistrationService } from 'src/app/core/service/registration.service';
import { UntypedFormBuilder, Validators, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { Component, OnInit, Input, ElementRef, EventEmitter, Output, ViewEncapsulation, ViewChild, OnDestroy } from '@angular/core';
import { UserService } from 'src/app/core/service/user.service';
import { User } from 'src/app/user/model/user';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { ImageService } from 'src/app/core/service/image.service';
import { UserValidators } from 'src/app/core/validators/user.validators';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { TranslateService } from '@ngx-translate/core';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserSetting } from '../model/user-setting';
import { Kno2DocumentSource } from 'src/app/core/model/kno2-document-source';
import { Kno2Service } from 'src/app/core/service/kno2.service';
import { sortBy, reject } from 'lodash';
import { Organization } from 'src/app/core/model/organization';
import { LanguageService } from 'src/app/core/service/language.service';
import { SelectEvent } from '@progress/kendo-angular-layout/tabstrip/events/select-event';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import copyToClipboard from 'copy-to-clipboard';
import { ComboBoxComponent, MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { Subscription } from 'rxjs';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';

@Component({
  standalone: false,
  selector: 'app-user-detail',
  templateUrl: './user-detail.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: [`
  .k-upload .k-actions, .k-upload .k-action-buttons { display: none; }
  .k-upload-files { display:none; }
  .k-upload { background-color:#f5f6fb!important; }
  .k-dropzone-hint { display: none}
  .photo-container > img { margin: 0 auto; display: block; max-height: 175px; }
  .photo-container:hover > img { cursor: pointer; }
  .container-organizations { max-height: 428px; overflow-y: auto; }
  img { max-height: 300px; }`]
})
export class UserDetailComponent implements OnInit, OnDestroy
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;
  @ViewChild('userRole') public userRole: ComboBoxComponent;
  @ViewChild('activeUserRoles') public activeUserRoles: MultiSelectComponent;
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

  @Input() public selectedUser: any;
  @Input() public isAdmin: boolean;
  @Output() public afterSave: EventEmitter<User> = new EventEmitter();

  public loadedSelectedUser: any;
  public userForm: any;
  public isNew = false;
  public states = [];
  public countries = [];
  public departments = [];
  public resultDepartments = [];
  public organizations = [];
  public imageToShow: any;
  public isNewImageSelected = false;
  public isImageDeleted = false;
  public isImageLoading = false;
  public loading = true;
  public initialLastName = '';
  public initialFirstName = '';
  public initialRoles = '';
  public isPasswordRequired = false;
  public isRegistrationTypeVisible = false;
  public userSetting: UserSetting;
  public preferredLanguages: Array<{ text: string; value: string }>;
  public roles: Array<{ text: string; value: string }>;
  public activeRoles: Array<{ text: string; value: string }>;
  public invalidFileExtensionText: string;
  public casesTasks: string;
  public channelsPrograms: string;
  public casesTasksChannelsPrograms: string;
  public userInUse: string;
  public isCommunityAdmin = false;
  public isProgramLead = false;
  public isReferralCoordinator = false;
  public showQRCodeDialog = false;
  public qrCodeImageUrl = null;
  public qrCodeKey = null;
  public invalidPasswordText = '';
  public showDesactivationWarning = false;
  public desactivationWarningContent: string;
  public initialEnabled = false;
  public revenueManager = false;
  public isOperator = false;
  public billingTabVisible = false;
  public noBillingOrgSelected: string;
  public unmatchDepartment: string;

  /**
   * All organizations
   */
  public allOrganizations = [];

  /**
   * Filtered list of organizations for drop-down.
   */
  public organizationsFiltered: Organization[] = [];
  public virtualOrganization: any = { itemHeight: 30 };

  // Image restrictions
  public fileRestrictions: FileRestrictions = {
    allowedExtensions: ['.jpg', '.jpeg', '.jpe', '.png'],
    maxFileSize: 0
  };

  // Username restrictions
  public minUsernameLength: number;
  public maxUsernameLength: number;
  public usernamePattern: string;
  /* [a-zA-Z0-9_-] # Match characters and symbols in the list, a-z, A-Z, 0-9, underscore, hyphen
     {6,25} # Length at least 6 characters and maximum length of 25
  */

  // Password restrictions
  public minPasswordLength: number;
  public maxPasswordLength: number;
  public passwordPattern: string;
  public minPasswordLengthService: number;
  public maxPasswordLengthService: number;
  public passwordPatternService: string;
  /*
  ^(?=.*[a-z])(?=.*[A-Z])(?=.*\d.*)(?=.*\W.*)[a-zA-Z0-9\S]{6,20}$
   At least one uppercase letter, one lowercase letter, one number, one special character. Space is not allowed.
  */

  public passwordMeterColors: any[];
  public kno2Enabled = false;
  public twoStepVerificationEnabledNewUser = false;

  /**
   * Kno2 document sources.
   */
  public kno2DocumentSources: Array<Kno2DocumentSource>;
  public phoneNumberMask: string;
  public isPhoneRequired = false;
  public editingUserRole: string;
  public editingActiveUserRoles: string;
  public currentUserOrganization: any;
  public isProgramLeadRole = false;

  /**
   * Current logged in user
   */
  private currentUser: User;
  private isCurrentLoggedInUser: boolean;
  private messagingSubscription: Subscription;
  private revenueManagerEnabled = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private logger: NGXLogger,
    private userService: UserService,
    private messageHandlerService: MessageHandlerService,
    private elem: ElementRef,
    private organizationService: OrganizationService,
    private authService: AuthenticationService,
    private imageService: ImageService,
    private userValidators: UserValidators,
    private messengerService: MessengerService,
    private translate: TranslateService,
    private settingService: SettingService,
    private kno2Service: Kno2Service,
    private authenticationService: AuthenticationService,
    private languageService: LanguageService,
    private registrationService: RegistrationService)   // Do not remove. It needs to be imported in order to works in the validator
  {
    // Set password meter colors
    this.passwordMeterColors = this.userService.getPasswordMeterColors();

    // Roles and Langages
    this.roles = sortBy(this.userService.roles, ['text']);
    this.activeRoles = sortBy(this.userService.roles.filter(r => r.value !== 'ROLE_COMMUNITY_USER'), ['text']);
    this.preferredLanguages = this.userService.preferredLanguages;
  }

  public get isCommunityUserRole(): boolean
  {
    return this.role && this.role.value === 'ROLE_COMMUNITY_USER';
  }

  public get firstName(): UntypedFormControl
  {
    return this.userForm.get('firstName');
  }

  public get lastName(): UntypedFormControl
  {
    return this.userForm.get('lastName');
  }

  public get username(): UntypedFormControl
  {
    return this.userForm.get('username');
  }

  public get role(): UntypedFormControl
  {
    return this.userForm.get('role');
  }

  public get password(): UntypedFormControl
  {
    return this.userForm.get('password');
  }

  public get department(): UntypedFormControl
  {
    return this.userForm && this.userForm.get('department');
  }

  public get organization(): UntypedFormControl
  {
    return this.userForm.get('organization');
  }

  public get billingOrganizations(): UntypedFormControl
  {
    return this.userForm.get('billingOrganizations');
  }

  public get allBillingOrganizations(): UntypedFormControl
  {
    return this.userForm.get('allBillingOrganizations');
  }

  public get email(): UntypedFormControl
  {
    return this.userForm.get('email');
  }

  public get directAddress(): UntypedFormControl
  {
    return this.userForm.get('directAddress');
  }

  public get photo(): UntypedFormControl
  {
    return this.userForm.get('photo');
  }

  public get registrationType(): UntypedFormControl
  {
    return this.userForm.get('registrationType');
  }

  public get preferredLanguage(): UntypedFormControl
  {
    return this.userForm.get('preferredLanguage');
  }

  public get servicesAccount(): UntypedFormControl
  {
    return this.userForm.get('servicesAccount');
  }

  public get twoFa(): UntypedFormControl
  {
    return this.userForm.get('twoFa');
  }

  public get phoneNumber(): UntypedFormControl
  {
    return this.userForm.get('phoneNumber');
  }

  public get allowedRoles(): UntypedFormControl
  {
    return this.userForm.get('allowedRoles');
  }

  public get allowedOrganizations(): UntypedFormControl
  {
    return this.userForm.get('allowedOrganizations');
  }

  public get note(): UntypedFormControl
  {
    return this.userForm.get('note');
  }

  public get isLoading(): boolean
  {
    return this.loading;
  }

  public formValid(): boolean
  {
    return this.userForm && this.userForm.valid;
  }

  public formDirty(): boolean
  {
    return this.userForm && this.userForm.dirty;
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public ngOnInit(): void
  {
    this.loading = true;

    if (this.selectedUser == null)
    {
      this.selectedUser = new User();
      this.isNew = true;
    }

    this.currentUser = this.authenticationService.currentUser;
    this.isCommunityAdmin = this.currentUser && this.currentUser.role === 'ROLE_COMMUNITY_ADMIN';
    this.isReferralCoordinator = this.currentUser && this.currentUser.role === 'ROLE_REFERRAL_COORDINATOR';
    this.isProgramLead = this.currentUser && this.currentUser.role === 'ROLE_PROGRAM_LEAD';
    this.revenueManager = this.currentUser && this.currentUser.role === 'ROLE_REVENUE_MANAGER_OPERATOR';
    this.isOperator = this.currentUser && this.currentUser.role === 'ROLE_OPERATOR' || this.currentUser.role === 'ROLE_SYSTEM_OPERATOR';
    this.billingTabVisible = this.revenueManagerEnabled &&
      (this.revenueManager || this.isOperator) && ((this.selectedUser.role === 'ROLE_CBO_REVENUE_ANALYST' || this.selectedUser.allowedRoles.includes('ROLE_CBO_REVENUE_ANALYST')));

    if (this.revenueManager)
    {
      this.roles = this.roles.filter(r => r.value === 'ROLE_REVENUE_MANAGER_OPERATOR' || r.value === 'ROLE_REVENUE_MANAGER_ANALYST' || r.value === 'ROLE_CBO_REVENUE_ANALYST');
      this.activeRoles = this.activeRoles.filter(r => r.value === 'ROLE_REVENUE_MANAGER_OPERATOR' || r.value === 'ROLE_REVENUE_MANAGER_ANALYST' || r.value === 'ROLE_CBO_REVENUE_ANALYST');
    }
    else
      if ((!this.revenueManager) && (!this.isOperator))
      {
        this.roles = this.roles.filter(r => r.value !== 'ROLE_REVENUE_MANAGER_OPERATOR' && r.value !== 'ROLE_REVENUE_MANAGER_ANALYST' && r.value !== 'ROLE_CBO_REVENUE_ANALYST');
        this.activeRoles = this.activeRoles.filter(r => r.value !== 'ROLE_REVENUE_MANAGER_OPERATOR' && r.value !== 'ROLE_REVENUE_MANAGER_ANALYST' && r.value !== 'ROLE_CBO_REVENUE_ANALYST');
      }

    this.translate.get(['INVALID-FILE-TYPE', 'USER-IN-USE-CASES-TASKS-CANNOT-DISABLE', 'USER-IN-USE-CHANNELS-PROGRAMS-CANNOT-DISABLE',
      'USER-IN-USE-CASES-TASKS-CHANNELS-PROGRAMS-CANNOT-DISABLE', 'USER-IN-USE-CANNOT-DELETE', 'NO-BILLING-ORG-SELECTED',
      'USER-ORGANIZATION-UNMATCHED-DEPARTMENT']).subscribe({
        next: (res) =>
        {
          this.invalidFileExtensionText = res['INVALID-FILE-TYPE'];
          this.casesTasks = res['USER-IN-USE-CASES-TASKS-CANNOT-DISABLE'];
          this.channelsPrograms = res['USER-IN-USE-CHANNELS-PROGRAMS-CANNOT-DISABLE'];
          this.casesTasksChannelsPrograms = res['USER-IN-USE-CASES-TASKS-CHANNELS-PROGRAMS-CANNOT-DISABLE'];
          this.noBillingOrgSelected = res['NO-BILLING-ORG-SELECTED'];
          this.unmatchDepartment = res['USER-ORGANIZATION-UNMATCHED-DEPARTMENT'];
        }
      });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'organization-changed') 
        {
          this.loadUser(true);
        }
      }
    });
    this.loadUser();
  }

  public save(): void
  {
    this.selectedUser.enabled = this.userForm.controls.enabled.value;
    this.selectedUser.servicesAccount = this.userForm.controls.servicesAccount.value;
    this.selectedUser.ehrOnlyAccess = this.userForm.controls.ehrOnlyAccess.value;
    this.selectedUser.twoFa = this.userForm.controls.twoFa.value;
    this.selectedUser.lastName = this.userForm.controls.lastName.value;
    this.selectedUser.firstName = this.userForm.controls.firstName.value;
    this.selectedUser.username = this.userForm.controls.username.value;
    this.selectedUser.organization = this.userForm.controls.organization.value;
    this.selectedUser.department = this.userForm.controls.department.value;
    this.selectedUser.email = this.userForm.controls.email.value;
    this.selectedUser.directAddress = this.userForm.controls.directAddress.value;
    this.selectedUser.password = this.userForm.controls.password.value;
    this.selectedUser.registrationType = this.userForm.controls.registrationType.value;
    this.selectedUser.preferredLanguage = this.userForm.controls.preferredLanguage.value;
    this.selectedUser.role = this.userForm.controls.role.value;
    this.selectedUser.phoneNumber = this.userForm.controls.phoneNumber.value;
    this.selectedUser.allowedRoles = this.userForm.controls.allowedRoles.value;
    this.selectedUser.allowedOrganizations = this.userForm.controls.allowedOrganizations.value;
    this.selectedUser.note = this.userForm.controls.note.value;

    if (this.billingTabVisible)
    {
      this.selectedUser.allBillingOrganizations = this.userForm.controls.allBillingOrganizations.value === undefined || this.userForm.controls.allBillingOrganizations.value === null ? false : this.userForm.controls.allBillingOrganizations.value;
      if (this.selectedUser.allBillingOrganizations === false && this.userForm.controls.billingOrganizations.value.length === 0)
      {
        this.messageHandlerService.displayDirectMessage('error', this.noBillingOrgSelected);
        if (!this.isNew && !this.servicesAccount.value && this.selectedUser.userIssuers.length > 0) 
        {
          this.tabstrip.selectTab(2);
        }
        else
        {
          this.tabstrip.selectTab(1);
        }
        return;
      }

      if (this.selectedUser.allBillingOrganizations)
      {
        this.selectedUser.billingOrganizations = [];
      }
      else
      {
        const billingOrganizations = [];
        if (this.userForm.controls.billingOrganizations.value !== null && this.userForm.controls.billingOrganizations.value.length > 0)
        {
          this.userForm.controls.billingOrganizations.value.forEach((org: Organization) =>
          {
            let currentId = null;
            let currentVersion = 0;
            if (this.loadedSelectedUser.billingOrganizations) 
            {
              const existingOrganization: any = this.loadedSelectedUser.billingOrganizations.find(x => x.organizationId === org.id);
              if (existingOrganization)
              {
                currentId = existingOrganization.id;
                currentVersion = existingOrganization.version;
              }
            }
            billingOrganizations.push({
              id: currentId,
              version: currentVersion,
              organizationId: org.id,
              userId: this.selectedUser.id !== undefined ? this.selectedUser.id : 0
            });
          });
          this.selectedUser.billingOrganizations = billingOrganizations;
        }
      }
    }

    if (!this.selectedUser.enabled && !this.isNew && this.initialEnabled)
    {
      this.userService.userInUseCounts(this.selectedUser.id).subscribe({
        next: (data) =>
        {
          if ((data.cases > 0 || data.tasks > 0) && (data.channels === 0))
          {
            this.desactivationWarningContent = this.casesTasks;
            this.showDesactivationWarning = true;
            return;
          }
          else if (data.channels > 0)
          {
            this.userService.getProgramsChannelsPerUser(this.selectedUser.id).subscribe({
              next: (channels) =>
              {
                let channelsText = '';
                channels.forEach(c => channelsText += c + '<br/>');
                let messageText = '';
                if (data.cases > 0 || data.tasks > 0)
                {
                  messageText = this.casesTasksChannelsPrograms.replace('{{channelsList}}', channelsText);
                }
                else
                {
                  messageText = this.channelsPrograms.replace('{{channelsList}}', channelsText);
                }
                this.copyChannelsList(channelsText.replace(/<br\/>/g, '\r\n'));
                this.desactivationWarningContent = messageText;
                this.showDesactivationWarning = true;
              },
              error: (errorResponse) =>
              {
                this.messageHandlerService.displayError(errorResponse);
              }
            });
          }
          else
          {
            this.saveUser();
          }
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    else
    {
      this.saveUser();
    }
  }

  public deleteImage(): void
  {
    this.getDefaultImageFromService();
    this.isImageDeleted = true;
    this.isNewImageSelected = false;
    this.photo.markAsDirty();
  }

  public setUserImage(e: UploadEvent): void
  {
    e.files.forEach((file) =>
    {
      // Validate file size
      if ((file.size / 1024) > this.fileRestrictions.maxFileSize)
      {
        this.messageHandlerService.displayError('FILE-SIZE-EXCEED-LIMIT');
        return;
      }

      // Validate file extension
      let validExtension = false;
      this.fileRestrictions.allowedExtensions.forEach((ext: string) =>
      {
        if (file.name.toLowerCase().indexOf(ext) !== -1)
        {
          validExtension = true;
        }
      });
      if (!validExtension)
      {
        this.messageHandlerService.displayDirectMessage('error',
          this.invalidFileExtensionText.replace('{{value}}', this.fileRestrictions.allowedExtensions.join()));
        return;
      }
      this.createImageFromBlob(file.rawFile, true);
    });
  }

  public selectPhoto(): void
  {
    const uploadButton = this.elem.nativeElement.querySelectorAll('input[name="files"]');
    uploadButton[0].click();
  }

  public handleOrganizationChange(value: Organization, isInitialization = false): void
  {
    if (value === undefined)
    {
      if (this.department !== undefined)
      {
        this.department.setValue(null);
        this.department.disable();
      }
      this.resultDepartments = [];
    }
    else
    {
      this.resultDepartments = this.departments.filter(s => s.organizationId === value.id);
      if (!isInitialization)
      {
        this.department.setValue(null);
      }
      if (this.department !== undefined)
      {
        this.department.enable();
      }
    }
  }

  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  public openQRCodeDialog(): void
  {
    this.getQRCode();
  }

  public generateNewSecret(): void
  {
    this.getQRCode(true);
  }

  public deleteUserIssuer(userIssuer: any): void
  {
    this.selectedUser.userIssuers = this.selectedUser.userIssuers.filter(e => e.id !== userIssuer.id);
    if (this.selectedUser.userIssuers.length === 0)
    {
      this.tabstrip.selectTab(0);
      this.setPhotoStyles();
    }
    this.userForm.markAsDirty();
  }

  public onTabSelect(e: SelectEvent): void
  {
    if (e.index === 0)
    {
      this.setPhotoStyles();
    }
  }

  public copyChannelsList(channelsText: string): void
  {
    copyToClipboard(channelsText);
  }

  public desactivationWarningSave(continueSave: boolean): void
  {
    this.showDesactivationWarning = false;
    this.desactivationWarningContent = undefined;
    if (continueSave)
    {
      setTimeout(() => { this.saveUser(); });
    }
  }

  /**
   * Filters the organizations based on user input.
   */
  public filterOrganizations(filter: string): void
  {
    this.organizationsFiltered = this.allOrganizations
      .filter(fa => fa.name.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Add organization setting to editForm
   *
   * @param id Setting id.
   * @param organization Organization.
   * @param value Setting value.
   */
  public addOrganization(id: number = null, version: number = null, organizationId: number = null, departmentId: number = null): void
  {
    this.userForm.controls.allowedOrganizations.push(this.initOrganization(id, version, organizationId, departmentId));
  }

  /**
  * Remove organization setting entry.
  *
  * @param i Row index
  * @param id Setting id
  */
  public removeOrganization(i: number, id: number): void
  {
    const control = this.userForm.controls.allowedOrganizations;
    // If this id was already persisted, we must remove
    if (id > 0)
    {
      const rejectedId: any = {};
      rejectedId.id = id;
      this.userForm.allowedOrganizations = reject(this.selectedUser.allowedOrganizations, rejectedId);
    }
    control.removeAt(i);
    this.userForm.markAsDirty();
  }

  public getAllowedDepartments(i: number): any[]
  {
    const selectedOrganizationId = this.userForm.controls.allowedOrganizations.controls[i].controls.organizationId?.value;
    return this.departments.filter(s => s.organizationId === selectedOrganizationId);
  }

  public showTooltip(e: ElementRef): void
  {
    this.tooltipDir.toggle(e, true);
  }

  public hideTooltip(): void
  {
    this.tooltipDir.hide();
  }

  /**
   * Create the organization setting form object.
   *
   * @param id Setting id.
   * @param organization Organization.
   * @param value Setting value.
   */
  private initOrganization(id: number = null, version: number = null, organizationId: number = null, departmentId: number = null): UntypedFormGroup
  {
    const organizationFormGroup = this.formBuilder.group({
      id: [id],
      version: [version],
      organizationId: [{ value: organizationId, disabled: false }, { validators: [Validators.required] }],
      departmentId: [{ value: departmentId, disabled: false }],
    });

    // Clear department on organization change
    organizationFormGroup.get('organizationId').valueChanges.subscribe(() =>
    {
      organizationFormGroup.get('departmentId').setValue(null);
    });

    return organizationFormGroup;
  }

  private saveUser(): void
  {
    // Reset twoFa if service account
    if (this.selectedUser.servicesAccount)
    {
      this.selectedUser.twoFa = false;
    }

    if (this.selectedUser.serviceAccount || this.isCommunityUserRole)
    {
      this.selectedUser.allowedRoles = [];
      this.selectedUser.allowedOrganizations = [];
      this.selectedUser.ehrOnlyAccess = false;
    }
    else
    {
      this.selectedUser.allowedOrganizations = this.allowedOrganizations.value;
    }

    // Validate if the selected User Role is part of the Allowed Roles when more than one login roles selected
    if (this.allowedRoles && this.allowedRoles.value)
    {
      if (this.allowedRoles.value.length === 1)
      {
        this.messageHandlerService.displayError('ACTIVE-ROLE-NOT-EMPTY-ERROR');
        this.activeUserRoles.focus();
        return;
      }

      if (this.allowedRoles.value.length > 0 && !this.allowedRoles.value.includes(this.role.value))
      {
        this.messageHandlerService.displayError('ACTIVE-ROLE-MISSING-ERROR');
        this.activeUserRoles.focus();
        return;
      }
    }

    // Validate if the selected User Organization is part of the Allowed Organizations when more than one login organizations selected
    if (this.userForm.controls.allowedOrganizations.length > 0)
    {
      if (this.userForm.controls.allowedOrganizations.length === 1)
      {
        this.messageHandlerService.displayError('ACTIVE-ORGANIZATION-NOT-EMPTY-ERROR');
        this.focusAllowedOrganizationsTab();
        return;
      }

      const matchingAllowedOrganization = this.userForm.controls.allowedOrganizations.controls.find((x: any) => x.controls.organizationId.value === this.organization?.value?.id);

      // Check if selected organization is part of the allowedOrganizations
      if (!matchingAllowedOrganization)
      {
        this.messageHandlerService.displayError('ACTIVE-ORGANIZATION-MISSING-ERROR');
        this.focusAllowedOrganizationsTab();
        return;
      }

      // Validate if selected unique orgazizations
      const listOrganizations = [];
      let dupOrganization = false;
      this.userForm.controls.allowedOrganizations.controls.forEach((x: any) =>
      {
        if (x.controls.organizationId.value)
        {
          if (listOrganizations.indexOf(x.controls.organizationId.value) !== -1)
          {
            dupOrganization = true;
          }
          else
          {
            listOrganizations.push(x.controls.organizationId.value);
          }
        }
      });

      if (dupOrganization)
      {
        this.messageHandlerService.displayError('ACTIVE-ORGANIZATION-DUP-ORG-ERROR');
        this.focusAllowedOrganizationsTab();
        return;
      }

      // Validate if department selection matched
      if (this.organization.value)
      {
        const currentDepartmentId = this.department.value?.id ? this.department.value?.id : null;
        const matchingDepartmentId = matchingAllowedOrganization.controls.departmentId.value ? matchingAllowedOrganization.controls.departmentId.value : null;
        if (matchingAllowedOrganization && matchingDepartmentId !== currentDepartmentId)
        {
          // If not admin (from account), organization is disabled. Therefore we can update the department for the default organization
          if (!this.isAdmin)
          {
            matchingAllowedOrganization.controls.departmentId.setValue(currentDepartmentId);
            this.selectedUser.allowedOrganizations = this.userForm.controls.allowedOrganizations.value;
          }
          else
          {
            this.messageHandlerService.displayDirectMessage('error',
              this.unmatchDepartment.replace('{{organization}}', this.organization.value.name));
            this.focusAllowedOrganizationsTab();
            return;
          }
        }
      }
    }

    // Prevent saving a community user role from Channels.
    if ((this.isNew && this.selectedUser.role === 'ROLE_COMMUNITY_USER') ||
      (!this.isNew && (
        (this.editingUserRole === 'ROLE_COMMUNITY_USER' && this.selectedUser.role !== 'ROLE_COMMUNITY_USER') ||
        (this.editingUserRole !== 'ROLE_COMMUNITY_USER' && this.selectedUser.role === 'ROLE_COMMUNITY_USER'))))
    {
      this.messageHandlerService.displayError('COMMUNITY-ROLE-ERROR');
      this.userRole.focus();
      return;
    }

    // Prevent a Program Lead to create an operator account
    if (this.isProgramLead && this.isAdmin) 
    {
      // Validate current role
      if ((this.isNew && (this.selectedUser.role === 'ROLE_OPERATOR' || this.selectedUser.role === 'ROLE_SYSTEM_OPERATOR')) ||
        (!this.isNew && (
          (this.selectedUser.role === 'ROLE_OPERATOR' || this.selectedUser.role === 'ROLE_SYSTEM_OPERATOR') &&
          (this.editingUserRole !== 'ROLE_OPERATOR' && this.editingUserRole !== 'ROLE_SYSTEM_OPERATOR')))) 
      {
        this.messageHandlerService.displayError('CANNOT-SAVE-USER-OPERATOR');
        this.userRole.focus();
        return;
      }

      // Validate active user roles
      if ((this.isNew && (this.selectedUser.allowedRoles.includes('ROLE_OPERATOR') || this.selectedUser.allowedRoles.includes('ROLE_SYSTEM_OPERATOR'))) ||
        (!this.isNew && (
          (this.selectedUser.allowedRoles.includes('ROLE_OPERATOR') || this.selectedUser.allowedRoles.includes('ROLE_SYSTEM_OPERATOR')) &&
          (!this.editingActiveUserRoles.includes('ROLE_OPERATOR') && !this.editingActiveUserRoles.includes('ROLE_SYSTEM_OPERATOR'))))) 
      {
        this.messageHandlerService.displayError('CANNOT-SAVE-USER-OPERATOR');
        this.activeUserRoles.focus();
        return;
      }
    }

    // Make sure the username or password is blank if registration by email
    if (this.selectedUser.registrationType === 'registration' && !this.selectedUser.inRegistration)
    {
      this.selectedUser.username = null;
      this.selectedUser.password = null;
    }

    if (this.selectedUser.organization === undefined)
    {
      this.selectedUser.organization = null;
    }

    if (this.selectedUser.department === undefined)
    {
      this.selectedUser.department = null;
    }

    if (this.isNewImageSelected)
    {
      const base64 = this.imageToShow.split('base64,')[1];
      this.selectedUser.photo = base64;
    }
    else if (this.isImageDeleted)
    {
      this.selectedUser.deleteImage = true;
    }
    else
    {
      this.selectedUser.photo = null;
    }

    // Remove organization and department for community user
    if (this.selectedUser.role === 'ROLE_COMMUNITY_USER')
    {
      this.selectedUser.organization = null;
      this.selectedUser.Department = null;
    }

    if (this.selectedUser.organization !== null)
    {
      delete this.selectedUser.organization.departments;
    }

    // user service to modify user
    this.userService.saveUser(this.selectedUser, this.isNew, this.selectedUser.id).subscribe({
      next: data =>
      {
        this.messageHandlerService.displaySuccess('USER-SAVED-SUCCESSFULLY');

        if (!this.isAdmin)
        {
          this.userService.getUserById(this.currentUser.id, true).subscribe({
            next: (user) =>
            {
              this.selectedUser = user;
              this.buildForm();
            }
          });
        }
        else
        {
          this.selectedUser = data;
        }

        // Emit afterSave event if set
        if (this.afterSave)
        {
          this.afterSave.emit(data);
        }

        // Handle User Account page if updating the current logged in user
        if (this.isCurrentLoggedInUser)
        {
          // If image changed, send header component message to refresh
          if (this.photo.dirty)
          {
            this.messengerService.sendMessage(new MessageSubject('userPhotoChanged'));
          }

          // if First or Last Name changed, send header component message to refresh
          if ((this.initialFirstName !== this.selectedUser.firstName) ||
            (this.initialLastName !== this.selectedUser.lastName))
          {
            const usernameMessagingSubject = new MessageSubject('userNameChanged',
              `${this.selectedUser.firstName} ${this.selectedUser.lastName}`);
            this.messengerService.sendMessage(usernameMessagingSubject);

            // Save new First/Last Name
            this.saveInitialFirstLastName();
          }

          if (this.initialRoles !== this.selectedUser.allowedRoles)
          {
            this.messengerService.sendMessage(new MessageSubject('userRolesChanged'));
            this.saveInitialRoles();
          }

          // Mark form Pristine
          this.userForm.markAsPristine();
        }
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError(error);
      }
    });
  }

  private getQRCode(generateNew: boolean = null): void
  {
    this.userService.getUserQRCodeById(this.selectedUser.id, generateNew).subscribe({
      next: (data) =>
      {
        this.qrCodeImageUrl = data.qrCodeImage;
        this.qrCodeKey = data.key;
        if (this.qrCodeImageUrl !== null)
        {
          this.showQRCodeDialog = true;
        }
        else
        {
          this.messageHandlerService.displayError('NO-QRCODE');
        }
      }
    });
  }

  private getUserPhoto()
  {
    if (this.isNew || !this.selectedUser.photo)
    {
      this.getDefaultImageFromService();
    }
    else
    {
      this.isImageLoading = true;
      const base64 = this.selectedUser.photo;
      this.imageToShow = `data:image/jpeg;base64,${base64}`;
      this.isImageLoading = false;
      this.setPhotoStyles();
    }
  }

  private getDefaultImageFromService()
  {
    this.isImageLoading = true;
    this.imageService.getImage('assets/images/no-photo.jpg').subscribe({
      next: (data) =>
      {
        this.createImageFromBlob(data, false);
        this.isImageLoading = false;
      },
      error: (error) =>
      {
        this.isImageLoading = false;
        this.logger.error(error);
      }
    });
  }

  private createImageFromBlob(image: Blob, isNewImage: boolean)
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      this.imageToShow = reader.result;
      this.isNewImageSelected = isNewImage;
      if (isNewImage)
      {
        this.isImageDeleted = false;
        this.photo.markAsDirty();
      }
      this.setPhotoStyles();
    }, false);
    if (image)
    {
      reader.readAsDataURL(image);
    }
  }

  private getDepartments()
  {
    this.organizationService.getCachedDepartments().subscribe({
      next: data =>
      {
        this.departments = sortBy(data, 'name');
        const organization = this.selectedUser.organization ? this.selectedUser.organization : undefined;
        this.handleOrganizationChange(organization, true);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private buildForm(): void
  {
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
        this.allOrganizations = data;
        this.filterOrganizations('');

        const currentUser = this.authService.currentUser;
        // Can't desactivate the current logged in user
        this.isCurrentLoggedInUser = (currentUser.userName === this.selectedUser.username);
        this.editingUserRole = this.selectedUser.role;
        this.editingActiveUserRoles = this.selectedUser.allowedRoles;

        // Set username validators
        const usernameValidators = [
          Validators.required,
          Validators.minLength(this.minUsernameLength),
          Validators.pattern(this.usernamePattern)
        ];

        // Set username async validators
        const usernameAsyncValidators = [this.userValidators.validateUsernameUnique.bind(this)];

        // Set email validators
        let emailValidators = [];
        if (!this.selectedUser.servicesAccount)
        {
          emailValidators = [Validators.required];
        }

        // Set email async validators
        const emailAsyncValidators = [this.userValidators.validateEmailUnique.bind(this)];

        // Set direct address async validators
        const directAddressAsyncValidators = [this.userValidators.validateDirectAddressUnique.bind(this)];

        // Set Registration Type
        let registrationType = '';
        if (this.isNew)
        {
          registrationType = 'registration';
          this.selectedUser.enabled = false;
        }
        else
        {
          registrationType = this.selectedUser.username &&
            !this.selectedUser.inRegistration ? 'password' : 'registration';
        }

        this.isPasswordRequired = this.isNew;
        this.isRegistrationTypeVisible = this.isNew || (registrationType === 'registration');

        // Validators based on RegistrationType
        let currentUsernameValidators = [];
        let currentUsernameAsyncValidators = [];
        let currentEmailValidators = [];
        let currentEmailAsyncValidators = [];
        if (registrationType === 'password')
        {
          currentUsernameValidators = usernameValidators;
          currentUsernameAsyncValidators = usernameAsyncValidators;
        }
        currentEmailValidators = emailValidators;
        currentEmailAsyncValidators = emailAsyncValidators;

        const billingOrganizations: Organization[] = [];
        if (this.billingTabVisible && this.selectedUser.billingOrganizations !== null && this.selectedUser.billingOrganizations !== undefined)
        {
          this.selectedUser.billingOrganizations.forEach((org: any) =>
          {
            const fullOrganization: Organization = this.organizations.find(x => x.id === org.organizationId);
            billingOrganizations.push(fullOrganization);
          });
        }

        // Set form controls
        this.initialEnabled = this.selectedUser.enabled;
        this.userForm = this.formBuilder.group({
          enabled: {
            value: this.selectedUser.enabled,
            disabled: this.isCurrentLoggedInUser || registrationType === 'registration'
          },
          lastName: [this.selectedUser.lastName],
          firstName: [this.selectedUser.firstName],
          username: [
            { value: this.selectedUser.username, disabled: !this.isNew },
            {
              validators: currentUsernameValidators,
              asyncValidators: currentUsernameAsyncValidators
            }
          ],
          password: [null],
          organization: [
            {
              value: (this.selectedUser.organization === undefined && this.isCommunityAdmin ?
                this.currentUserOrganization : this.selectedUser.organization),
              disabled: !this.isAdmin
            }
          ],
          department: this.selectedUser.department,
          email: [
            { value: this.selectedUser.email, disabled: this.selectedUser.inRegistration },
            {
              validators: currentEmailValidators,
              asyncValidators: currentEmailAsyncValidators
            }
          ],
          directAddress: [
            { value: this.selectedUser.directAddress, disabled: false },
            {
              validators: [],
              asyncValidators: directAddressAsyncValidators
            }
          ],
          note: [this.selectedUser.note],
          photo: this.selectedUser.photo,
          registrationType: { value: registrationType, disabled: true },
          preferredLanguage: this.selectedUser.preferredLanguage,
          role: [
            {
              value: this.selectedUser.role,
              disabled: !this.isAdmin || (this.selectedUser && this.selectedUser.role === 'ROLE_COMMUNITY_USER')
            },
            {
              validators: Validators.required
            }
          ],
          allowedRoles: [
            {
              value: this.selectedUser.allowedRoles,
              disabled: !this.isAdmin || (this.selectedUser && this.selectedUser.role === 'ROLE_COMMUNITY_USER')
            }
          ],
          allowedOrganizations: this.formBuilder.array([]),
          servicesAccount: {
            value: this.selectedUser.servicesAccount,
            disabled: registrationType === 'registration' && !this.isNew
          },
          ehrOnlyAccess: {
            value: this.selectedUser.ehrOnlyAccess,
            disabled: !this.isAdmin
          },
          twoFa: {
            value: this.isNew ? this.twoStepVerificationEnabledNewUser : this.selectedUser.twoFa,
            disabled: this.currentUser.role !== 'ROLE_OPERATOR' && this.currentUser.role !== 'ROLE_PROGRAM_LEAD'
          },
          phoneNumber: [
            { value: this.selectedUser.phoneNumber, disabled: this.selectedUser.inRegistration }
          ],
          allBillingOrganizations: {
            value: this.billingTabVisible ? this.selectedUser.allBillingOrganizations : null,
            disabled: !this.isAdmin
          },
          billingOrganizations: [
            { value: this.billingTabVisible ? billingOrganizations : '', disabled: !this.isAdmin }
          ],
        });

        if (this.billingTabVisible)
        {
          const allBillingOrganizations = this.userForm.controls.allBillingOrganizations.value === undefined || this.userForm.controls.allBillingOrganizations.value === null ? false : this.userForm.controls.allBillingOrganizations.value;
          if (allBillingOrganizations === true)
          {
            this.userForm.get('billingOrganizations').disable();
          }
        }

        // Create form elements for setting per organizations
        this.selectedUser.allowedOrganizations.forEach((data: any) =>
        {
          this.addOrganization(data.id, data.version, data.organizationId, data.departmentId);
        });

        if (this.isNew && this.currentUserOrganization) 
        {
          this.organization.setValue(this.currentUserOrganization);
          this.selectedUser.organization = this.currentUserOrganization;
          this.getDepartments();
        }

        this.isPhoneRequired = this.selectedUser.twoFa;

        this.userForm.get('role').valueChanges.subscribe({
          next: (value: string) =>
          {
            this.setOrganizationControlState();
            this.setPhotoStyles();
            this.billingTabVisible = this.revenueManagerEnabled && (this.revenueManager || this.isOperator) && (value === 'ROLE_CBO_REVENUE_ANALYST');
          }
        });

        this.userForm.get('allowedRoles').valueChanges.subscribe({
          next: (value: string) =>
          {
            this.billingTabVisible = this.revenueManagerEnabled && (this.revenueManager || this.isOperator) && (value.includes('ROLE_CBO_REVENUE_ANALYST'));
          }
        });

        // Subscribe on registrationType changes to handle validators requirements
        this.userForm.get('registrationType').valueChanges.subscribe({
          next: (selectedRegistrationType: string) =>
          {
            if (selectedRegistrationType === 'password')
            {
              this.userForm.get('username').enable();
              this.userForm.get('username').setValidators(usernameValidators);
              this.userForm.get('username').setAsyncValidators(usernameAsyncValidators);
            }
            else
            {
              this.userForm.get('username').disable();
              this.userForm.get('username').setValidators([]);
              this.userForm.get('username').setAsyncValidators([]);
            }
            this.isPasswordRequired = (selectedRegistrationType === 'password');
            this.userForm.get('enabled').setValue(selectedRegistrationType === 'password');
            this.userForm.get('username').updateValueAndValidity();
            this.userForm.get('enabled').updateValueAndValidity();
            this.setPasswordValidators();
          }
        });

        this.userForm.get('servicesAccount').valueChanges.subscribe({
          next: (servicesAccount: boolean) =>
          {
            this.userForm.get('email').setValidators(servicesAccount ? [] : [Validators.required]);
            this.userForm.get('email').updateValueAndValidity();

            if (this.isNew)
            {
              if (servicesAccount)
              {
                this.userForm.get('registrationType').setValue('password');
              }
              else
              {
                this.userForm.get('registrationType').setValue('registration');
              }
              this.userForm.get('registrationType').updateValueAndValidity();
            }

            if (servicesAccount)
            {
              this.userForm.get('allowedRoles').setValue([]);
              this.userForm.get('allowedRoles').updateValueAndValidity();

              this.userForm.get('allowedOrganizations').setValue([]);
              this.userForm.get('allowedOrganizations').updateValueAndValidity();
            }

            this.setUserNamesValidators();
            this.setPasswordValidators();
            this.setOrganizationControlState();
          }
        });

        this.setUserNamesValidators();
        this.setPasswordValidators();
        this.setOrganizationControlState();

        // Keep First and Last name to trigger changes after save
        this.saveInitialFirstLastName();
        this.saveInitialRoles();

        this.userForm.get('twoFa').valueChanges.subscribe({
          next: (value: string) =>
          {
            // Community User is using SMS two factor auth
            if (value && this.isCommunityUserRole)
            {
              this.userForm.get('phoneNumber').setValidators([Validators.required]);
              this.isPhoneRequired = true;
            }
            else
            {
              this.userForm.get('phoneNumber').setValidators([]);
              this.isPhoneRequired = false;
            }
            this.userForm.get('phoneNumber').updateValueAndValidity();
          }
        });

        this.userForm.get('allBillingOrganizations').valueChanges.subscribe({
          next: (value: boolean) =>
          {
            if (value)
            {
              this.userForm.get('billingOrganizations').disable();
              this.userForm.get('billingOrganizations').setValue(null);
            }
            else
            {
              this.userForm.get('billingOrganizations').enable();
            }
          }
        });
        this.loadedSelectedUser = this.selectedUser;
        this.loading = false;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private setPasswordValidators(): void
  {
    // Set password validators
    const passwordValidators = [
      Validators.minLength(this.minPasswordLength),
      Validators.pattern(this.passwordPattern)
    ];

    const passwordValidatorsService = [
      Validators.minLength(this.minPasswordLengthService),
      Validators.pattern(this.passwordPatternService)
    ];

    // If new Service Account, password is required.
    if (this.isNew)
    {
      passwordValidatorsService.push(Validators.required);
    }

    if (this.userForm.get('registrationType').value === 'password' || this.userForm.get('servicesAccount').value)
    {
      if (this.userForm.get('servicesAccount').value)
      {
        this.userForm.get('password').setValidators(passwordValidatorsService);
      }
      else
      {
        this.userForm.get('password').setValidators(passwordValidators);
      }
    }
    else
    {
      this.userForm.get('password').setValidators([]);
    }
    this.userForm.get('password').setValue(null);
    this.userForm.get('password').updateValueAndValidity();
  }

  private setOrganizationControlState(): void
  {
    if (this.role.value !== 'ROLE_COMMUNITY_USER' && !this.servicesAccount.value)
    {
      this.userForm.get('organization').setValidators([Validators.required]);
    }
    else
    {
      this.userForm.get('organization').setValidators([]);
    }
    this.userForm.get('organization').updateValueAndValidity();
  }

  private setUserNamesValidators()
  {
    if (this.servicesAccount.value)
    {
      this.userForm.get('firstName').setValidators([]);
      this.userForm.get('lastName').setValidators([]);
    }
    else
    {
      this.userForm.get('firstName').setValidators([Validators.required]);
      this.userForm.get('lastName').setValidators([Validators.required]);
      this.getUserPhoto();
    }
    this.userForm.get('firstName').updateValueAndValidity();
    this.userForm.get('lastName').updateValueAndValidity();
  }

  private saveInitialFirstLastName()
  {
    this.initialFirstName = this.selectedUser.firstName;
    this.initialLastName = this.selectedUser.lastName;
  }

  private saveInitialRoles()
  {
    this.initialRoles = this.selectedUser.allowedRoles;
  }

  private getOrganizations()
  {
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
        this.allOrganizations = data;
        this.filterOrganizations('');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private setPhotoStyles()
  {
    setTimeout(() =>
    {
      const dropzones = this.elem.nativeElement.querySelectorAll('.k-dropzone');
      const photo = this.elem.nativeElement.querySelectorAll('.photo-container');
      const uploadButton = this.elem.nativeElement.querySelectorAll('.k-upload-button');
      if (dropzones.length > 0)
      {
        dropzones[0].appendChild(photo[0]);
        dropzones[0].setAttribute('style', 'display:contents!important;');
        uploadButton[0].setAttribute('style', 'display:none!important;');
        photo[0].setAttribute('style', 'background-color: #f5f6fb!important;');
      }
    });
  }

  private getUserSettings()
  {
    this.settingService.getCachedUserSettings().subscribe({
      next: (data) =>
      {
        this.userSetting = data;

        // Set username, password and image size from settings
        this.fileRestrictions.maxFileSize = this.userSetting.userImageMaxKB;
        this.minUsernameLength = this.userSetting.usernameMin;
        this.maxUsernameLength = this.userSetting.usernameMax;
        this.usernamePattern = this.userSetting.usernamePattern;
        this.minPasswordLength = this.userSetting.passwordMin;
        this.maxPasswordLength = this.userSetting.passwordMax;
        this.passwordPattern = this.userSetting.passwordPattern;
        this.minPasswordLengthService = this.userSetting.passwordMinService;
        this.maxPasswordLengthService = this.userSetting.passwordMaxService;
        this.passwordPatternService = this.userSetting.passwordPatternService;

        this.buildForm();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private focusAllowedOrganizationsTab(): void
  {
    if (!this.isNew && !this.servicesAccount.value && this.selectedUser.userIssuers.length > 0) 
    {
      this.tabstrip.selectTab(this.billingTabVisible ? 3 : 2);
    }
    else
    {
      this.tabstrip.selectTab(this.billingTabVisible ? 2 : 1);
    }
  }

  private loadUser(executeBuildForm: boolean = false): void
  {
    this.userService.getUserById(this.currentUser.id, true).subscribe({
      next: (user) =>
      {
        this.currentUserOrganization = user.organization;
        this.isProgramLeadRole = user.role === 'ROLE_PROGRAM_LEAD';

        this.settingService.getCachedSessionSettings().subscribe({
          next: (sessionSetting) =>
          {
            this.revenueManagerEnabled = sessionSetting.revenueManagerEnabled;
            this.kno2Enabled = sessionSetting.kno2Enabled;
            this.twoStepVerificationEnabledNewUser = sessionSetting.twoStepVerificationEnabledNewUser;
            if (this.kno2Enabled)
            {
              this.kno2Service.getCachedDocumentSources().subscribe({
                next: (documentSources) =>
                {
                  // Filter direct address on client since type not working on the back-end.
                  this.kno2DocumentSources = documentSources;
                }
              });
            }
            this.phoneNumberMask = sessionSetting.phoneMask;

            // Get Password Pattern Text
            const languageIndex = this.languageService.currentLanguage === 'es' ? 1 :
              this.languageService.currentLanguage === 'de' ? 2 : 0;
            this.invalidPasswordText = sessionSetting.passwordPatternTexts[languageIndex];
            this.getUserSettings();
            this.getDepartments();

            if (executeBuildForm)
            {
              this.selectedUser = user;
              this.buildForm();
            }
          }
        });
      }
    });
  }
}
